import CloseOutlined from '@ant-design/icons/CloseOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import Modal from 'antd/es/modal';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { styled } from 'styled-components';

import { useCreatePipeline } from '../../../queries/pipelines';
import { PipelineBasicsForm } from './PipelineBasicsForm';

const StyledModal = styled(Modal)`
  .ant-modal-header {
    margin-bottom: 0;
  }
`;

const ContentContainer = styled.div`
  padding: ${({ theme }) => theme.paddingLG}px 0;
  min-height: ${({ theme }) => theme.controlHeight * 8}px;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.marginXS}px;
`;

interface CreatePipelineModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  onSuccess?: (pipelineId: string) => void;
}

export interface PipelineFormData {
  name: string;
  description?: string;
}

const defaultFormData: PipelineFormData = {
  name: '',
  description: '',
};

export const CreatePipelineModal: React.FC<CreatePipelineModalProps> = ({
  open,
  onClose,
  teamId,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<PipelineFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createPipelineMutation = useCreatePipeline();

  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const pipeline = await createPipelineMutation.mutateAsync({
        teamId,
        name: values.name.trim(),
        description: values.description?.trim() || null,
      });

      void message.success(`Pipeline "${values.name}" created successfully!`);
      handleClose();

      if (onSuccess) {
        onSuccess(pipeline.pipelineId);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.error?.message || 'Failed to create pipeline';
        void message.error(errorMessage);
      } else {
        // Form validation error - form will show error messages
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.resetFields();
      onClose();
    }
  };

  return (
    <StyledModal
      open={open}
      onCancel={handleClose}
      width={600}
      footer={null}
      closeIcon={<CloseOutlined />}
      title="Create New Pipeline"
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={defaultFormData}
        onFinish={handleCreate}
      >
        <ContentContainer>
          <PipelineBasicsForm form={form} />
        </ContentContainer>

        <FooterContainer>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isSubmitting}>
            Create Pipeline
          </Button>
        </FooterContainer>
      </Form>
    </StyledModal>
  );
};

export default CreatePipelineModal;
